import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { formatNumber, formatCompactCurrency } from "@/lib/utils";
import { PageHeader } from "@/components/v2/page-header";
import { KPIRow } from "@/components/v2/kpi-row";
import type { KPICardData } from "@/components/v2/kpi-row";
import { EmptyState } from "@/components/v2/empty-state";
import { useAddDemoAccountStore } from "@/stores/add-demo-account-store";
import { useOpportunityPipelineSummary } from "@/hooks/use-api";

interface CountResponse {
  total: number;
  [key: string]: unknown;
}

function useEntityCount(path: string) {
  return useQuery({
    queryKey: ["count", path],
    queryFn: () => api.get<CountResponse>(`${path}?limit=1&offset=0`),
    select: (d) => d.total,
  });
}

export default function V2DashboardPage() {
  const { data: customerCount } = useEntityCount("/core/customers");
  const { data: jobCount } = useEntityCount("/ops/jobs");
  const { data: invoiceCount } = useEntityCount("/finance/invoices");
  const { openDialog } = useAddDemoAccountStore();

  // ADV-1481: the active-pipeline total is DB-computed (over ALL opportunities,
  // not one capped page) and read straight off the summary response — no
  // client-side reduce. Sharing the ["opportunity-pipeline-summary"] query key
  // also means the opportunity/proposal mutations that already invalidate it
  // refresh this KPI after an edit.
  const { data: pipelineData } = useOpportunityPipelineSummary();
  const pipelineValue = pipelineData?.active_pipeline_value;

  const kpis: KPICardData[] = [
    {
      label: "Pipeline",
      value: pipelineValue != null ? formatCompactCurrency(pipelineValue) : "—",
      href: "/opportunities",
    },
    {
      label: "Customers",
      value: customerCount != null ? formatNumber(customerCount) : "—",
      href: "/customers",
    },
    {
      label: "Jobs",
      value: jobCount != null ? formatNumber(jobCount) : "—",
      href: "/jobs",
    },
    {
      label: "Invoices",
      value: invoiceCount != null ? formatNumber(invoiceCount) : "—",
      href: "/invoices",
    },
  ];

  return (
    <>
      <PageHeader breadcrumbs={[{ label: "Dashboard" }]} title="Dashboard" />
      <KPIRow cards={kpis} />
      <div className="mt-6">
        <EmptyState
          title="Recent Activity"
          description="Activity tables and feeds will appear here."
          secondaryAction={{
            label: "Try Adva with sample data",
            onClick: openDialog,
          }}
        />
      </div>
    </>
  );
}
